import { type FocusEventHandler, type ReactNode, type Ref, useState } from "react";
import TextField from "@mui/material/TextField";
import { useTranslation } from "@privacy/translations";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import type { User } from "../api/users.types";
import { PersonIcon3 } from "./icons";
import type { AutocompleteOptionType } from "./Autocomplete";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  createFilterAutocompleteOption,
  CreateNewAutocompleteOption,
  userToAutocompleteOption,
} from "./Autocomplete";
import { CreateUserDialog } from "./CreateUserDialog";
import { usePermission } from "./permissions";
import { useCurrentOrganisation } from "../root/CurrentOrganisationProvider";

type OptionWithMaybeIsNew = AutocompleteOptionType & { isNew?: boolean; disabledReason?: string };
type CreateNewDialogState = { isOpen: true; value: string } | { isOpen: false };

type UserSelectProps<TUser extends User> = {
  id?: string;
  error?: boolean;
  selectedId: string | null | undefined;
  /** Undefined if the user is not loaded yet */
  users: Array<TUser> | undefined;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onSelectUser: (userId: string | undefined) => void;
  ref?: Ref<HTMLInputElement>;
  enableCreateNewUser?: boolean;
  getDisabledReason?: (user: TUser) => string | undefined;
  noOptionsText?: ReactNode;
  placeholder?: string;
  isClearable?: boolean;
};

export const UserSelect = <TUser extends User>({
  id,
  error,
  selectedId,
  onSelectUser,
  users,
  onBlur,
  ref,
  enableCreateNewUser = false,
  getDisabledReason,
  noOptionsText,
  placeholder,
  isClearable = false,
}: UserSelectProps<TUser>) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { scimEnabled } = useCurrentOrganisation();

  const [createNewDialogState, setCreateNewDialogState] = useState<CreateNewDialogState>({ isOpen: false });

  const options: OptionWithMaybeIsNew[] = users?.map(user => ({
    ...userToAutocompleteOption(user),
    disabledReason: getDisabledReason?.(user),
  })) ?? [AUTOCOMPLETE_LOADING_OPTION];

  const selected = options.find(option => option.value === selectedId) ?? null;

  const showAddNewUserOption = enableCreateNewUser && !scimEnabled && hasPermission("organization-api:user:create");

  return (
    <>
      <Autocomplete
        openOnFocus
        blurOnSelect
        clearOnBlur
        clearOnEscape
        clearText={t("UserSelect.Clear")}
        options={options}
        noOptionsText={noOptionsText ?? t("DynamicForm.PersonSelect.Empty")}
        isOptionEqualToValue={(option, selected) =>
          option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selected.value
        }
        getOptionDisabled={option => Boolean(option.disabledReason)}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          if (option.isNew) {
            return <CreateNewAutocompleteOption key={option.value} {...optionProps} option={option} />;
          }

          if (!option.disabledReason) {
            return <AutocompleteOption key={option.value} dense {...optionProps} option={option} />;
          }

          const { onClick, onMouseMove, onTouchStart, ...hoverableOptionProps } = optionProps;
          return (
            <Tooltip key={option.value} title={option.disabledReason} followCursor describeChild>
              <AutocompleteOption
                dense
                {...hoverableOptionProps}
                option={option}
                sx={{ [`&&.${autocompleteClasses.option}[aria-disabled="true"]`]: { pointerEvents: "auto" } }}
              />
            </Tooltip>
          );
        }}
        value={selected}
        id={id}
        onBlur={onBlur}
        onChange={(_, option) => {
          if (option && "isNew" in option) setCreateNewDialogState({ isOpen: true, value: option.value });
          else onSelectUser(option?.value || "");
        }}
        filterOptions={(sourceOption, params) => {
          const filtered: OptionWithMaybeIsNew[] = createFilterAutocompleteOption(sourceOption, params);
          if (!showAddNewUserOption) return filtered;
          return [
            ...filtered,
            {
              value: params.inputValue,
              label: params.inputValue
                ? t("UserSelect.AddNewUserWithPrefill", { inputValue: params.inputValue })
                : t("UserSelect.AddNewUser"),
              isNew: true,
            },
          ];
        }}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            error={error}
            placeholder={placeholder ?? t("UserSelect.Placeholder")}
            slotProps={{
              ...params.slotProps,

              input: {
                ...params.slotProps.input,
                startAdornment: selected && <PersonIcon3 />,
                endAdornment: (
                  <>
                    {selected?.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme => theme.palette.text.secondary,
                          fontSize: theme => theme.typography.pxToRem(10),
                          paddingY: 0,
                          mr: isClearable ? 6 : 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {selected?.description}
                      </Typography>
                    )}
                    {isClearable && params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
            inputRef={ref}
          />
        )}
      />
      {createNewDialogState.isOpen && (
        <CreateUserDialog
          defaultEmail={createNewDialogState.value}
          onCancel={() => setCreateNewDialogState({ isOpen: false })}
          onCreated={userId => {
            setCreateNewDialogState({ isOpen: false });
            onSelectUser(userId);
          }}
        />
      )}
    </>
  );
};
